import type { CallTranscriptMessage, TranscriptSnippet } from "@expert/shared-types";

import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { type GaiaWebSocketResponse, gaiaWsEventBus } from "@expert/gaia";
import { getLogger } from "@expert/logging";
import { useEffect, useEffectEvent, useRef } from "react";

import { getRootDispatcher } from "@/analytics";
import { usePartner } from "@/sdk";
import { mergeSnippets } from "@/transcript/utils/mergeSnippets";

const TRANSCRIPT_SNIPPET_LOG_INTERVAL = 10000;
// Re-arm window for malformed frames that carry no sessionId (can't be deduped per-call). Must stay
// comfortably above 3600s / (transcript alert threshold) so a single broken client can't trip the
// fleet alert on its own: the rule fires at 30/hr, i.e. one emit / 120s, so we use 300s → ≤12/hr per
// client. If the backend threshold changes materially, revisit this.
const SESSIONLESS_REEMIT_MS = 300_000;

const logger = getLogger({ module: "TranscriptSnippetListener" });

export const useTranscriptSnippetListener = (
    setSnippets: (value: React.SetStateAction<TranscriptSnippet[] | undefined>) => void,
    // When provided, snippets whose `body.sessionId` !== `activeCallSid` are ignored; `activeCallSid` is
    // undefined once the call has ended, so all trailing snippets are dropped. Legacy callers pass no scope.
    callScope?: { activeCallSid?: string },
) => {
    // Emit the processing-failure metric at most once per session — a schema-drifted payload throws on
    // every websocket frame, and emitting per frame would let one broken call flood the metric and trip
    // the alert on its own. Dedupe by the message's `sessionId` (the call identifier): each failing call
    // emits once. The Set is bounded by the number of distinct failing sessions the component sees.
    const reportedSessionsRef = useRef<Set<string>>(new Set());
    // Frames with no readable sessionId (severe schema drift) can't be attributed to a call, so we can't
    // dedupe them per-call. Time-throttle instead: bounds the per-frame flood but still re-arms, so a
    // sustained sessionless outage is not silenced for the whole (long-lived, no-scope) hook lifetime.
    const sessionlessLastEmitRef = useRef(0);

    // The processing-failure metric feeds the ui-orchestration alert, which monitors the redesigned
    // voice workspace only. Failures still log everywhere; only redesign emits the metric.
    const { features } = useFeatures<WorkspaceFeatures>();
    const partner = usePartner();
    const transcriptMonitored = features.isVoiceWorkspaceRedesignV2Enabled;

    const onCallTranscript = useEffectEvent(({ body }: GaiaWebSocketResponse) => {
        try {
            const { transcripts, timestamp: gaiaTimestamp, sessionId } = body as CallTranscriptMessage;

            // Ignore snippets that don't belong to the active call (and all snippets once the call has ended).
            if (callScope && sessionId !== callScope.activeCallSid) return;

            if (transcripts) {
                const exwoTimestamp = Date.now();

                transcripts.forEach((t) => {
                    const shouldSend = Math.floor(Math.random() * TRANSCRIPT_SNIPPET_LOG_INTERVAL) + 1 === 1;
                    if (shouldSend) {
                        void getRootDispatcher().dispatchBusinessEvent("TranscriptSnippetReceived", {
                            gaiaTimestamp,
                            exwoTimestamp: exwoTimestamp,
                            snippetTimestamp: t.timestamp,
                        });
                    }
                });
                setSnippets((prevState) => mergeSnippets(prevState, transcripts));
            }
        } catch (error) {
            // A malformed/schema-drifted payload silently kills the live transcript. Log every frame,
            // but emit the metric at most once per call (deduped as set up above) so it can't flood.
            logger.error({ error }, "Transcript | failed to process snippet");
            if (!transcriptMonitored) return;

            const sessionId = (body as Partial<CallTranscriptMessage> | undefined)?.sessionId;
            let shouldEmit: boolean;
            if (sessionId != null) {
                shouldEmit = !reportedSessionsRef.current.has(sessionId);
                if (shouldEmit) reportedSessionsRef.current.add(sessionId);
            } else {
                const now = Date.now();
                shouldEmit = now - sessionlessLastEmitRef.current > SESSIONLESS_REEMIT_MS;
                if (shouldEmit) sessionlessLastEmitRef.current = now;
            }

            if (shouldEmit) void sendErrorMetric("TranscriptSnippetProcessingFailed", { partner });
        }
    });

    useEffect(() => gaiaWsEventBus.on("gaia_ws_call-transcript", onCallTranscript), []);
};
